import { CheckCircleIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import React from 'react'

import type { PaymentStatus } from '../../types'

export interface PurchaseStatusBadgeProps {
  /** 'own' matches the sender's dark/own-message context; 'other' matches the recipient's light/other-message context. */
  isMine: boolean
  /** 'paid' renders the "Sold"/"Purchased" + checkmark variant; otherwise falls back to plain amount text, or nothing. */
  paymentStatus?: PaymentStatus
  amountText?: string
}

/**
 * Bullet + amount/label + checkmark row for a paid attachment or message.
 * Shared by `LockedAttachment.Media`/`.Text`'s status badges.
 */
const PurchaseStatusBadge: React.FC<PurchaseStatusBadgeProps> = ({
  isMine,
  paymentStatus,
  amountText,
}) => {
  const labelColour = isMine ? 'text-white/55' : 'text-black/55'
  const successColour = isMine ? 'text-[#34c759]' : 'text-[#008236]'
  if (paymentStatus === 'paid') {
    return (
      <React.Fragment>
        <span className={classNames('text-xs font-medium', labelColour)}>
          &bull;
        </span>
        <span className={classNames('text-xs font-medium', successColour)}>
          {isMine ? 'Sold' : 'Purchased'}
        </span>
        <CheckCircleIcon
          className={classNames('shrink-0 size-3.5', successColour)}
          weight="bold"
          aria-hidden
        />
      </React.Fragment>
    )
  }

  if (amountText != null) {
    return (
      <React.Fragment>
        <span className={classNames('text-xs font-medium', labelColour)}>
          &bull;
        </span>
        <span className={classNames('text-xs font-medium', labelColour)}>
          {amountText}
        </span>
      </React.Fragment>
    )
  }

  if (paymentStatus === 'failed' || paymentStatus === 'refunded') {
    return (
      <React.Fragment>
        <span className={classNames('text-xs font-medium', labelColour)}>
          &bull;
        </span>
        <span className={classNames('text-xs font-medium', labelColour)}>
          {paymentStatus === 'failed' ? 'Payment failed' : 'Refunded'}
        </span>
      </React.Fragment>
    )
  }

  return null
}

export default PurchaseStatusBadge
